import React from 'react';
import { Box, Typography, Alert } from '@mui/material';
import ConfigCard from '../shared/ConfigCard';
import useUsers from '../../hooks/useUsers';
import useDecoyStatuses from '../../hooks/useDecoyStatuses';

function DecoysTab() {
  const { users: currentUsers } = useUsers();
  const { 
    decoyStatuses, 
    userStatuses,
    error: statusError,
    updateStatus 
  } = useDecoyStatuses();
  
  const [loadingDecoys, setLoadingDecoys] = React.useState({});
  const [decoyError, setDecoyError] = React.useState(null);
  const [expandedDecoys, setExpandedDecoys] = React.useState({});

  const decoysRequiringUsers = ['Protected Users Group', 'Remote Management Users Group', 'Remote Desktop Users Group', 'Powershell Restriction', 'Cmd Restriction'];

  const handleAttackToggle = async (decoyName, userName = null) => {
    setDecoyError(null);
    const isCurrentlyEnabled = userName 
      ? userStatuses[decoyName]?.[userName]?.enabled
      : decoyStatuses[decoyName]?.enabled;

    setLoadingDecoys(prev => ({ 
      ...prev, 
      [userName ? `${decoyName}-${userName}` : decoyName]: true 
    }));

    const success = await updateStatus(decoyName, !isCurrentlyEnabled, userName);
    
    if (!success) {
      setDecoyError(`Failed to ${isCurrentlyEnabled ? 'disable' : 'enable'} ${decoyName}${userName ? ` for user ${userName}` : ''}`);
    }
    
    setLoadingDecoys(prev => ({ 
      ...prev, 
      [userName ? `${decoyName}-${userName}` : decoyName]: false 
    }));
  };

  const toggleExpand = (decoyName) => {
    setExpandedDecoys(prev => ({
      ...prev,
      [decoyName]: !prev[decoyName]
    }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Decoy Configurations
      </Typography>
      
      {(decoyError || statusError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {decoyError || statusError}
        </Alert>
      )}
      
      {/* Attacks that don't require users */}
      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
        General Decoy Configurations
      </Typography>
      {Object.keys(decoyStatuses)
        .filter(decoyName => !decoysRequiringUsers.includes(decoyName))
        .map((decoyName) => (
          <ConfigCard
            key={decoyName}
            title={decoyName}
            enabled={decoyStatuses[decoyName]?.enabled || false}
            loading={loadingDecoys[decoyName]}
            onToggle={() => handleAttackToggle(decoyName)}
          />
        ))}

      {/* Attacks that require users */}
      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
        User-based Decoy Configurations
      </Typography>
      {Object.keys(userStatuses)

        .filter(decoyName => decoysRequiringUsers.includes(decoyName))
        .map((decoyName) => (
          <ConfigCard
            key={decoyName}
            title={decoyName}
            expanded={expandedDecoys[decoyName]}
            onExpand={() => toggleExpand(decoyName)}
            showExpandButton={true}
            showToggle={false}
          >
            <Box sx={{ mt: 2 }}>
              {currentUsers.map((user) => (
                <ConfigCard
                  key={`${decoyName}-${user.Name}`}
                  title={user.Name}
                  enabled={userStatuses[decoyName]?.[user.Name]?.enabled || false}
                  loading={loadingDecoys[`${decoyName}-${user.Name}`]}
                  onToggle={() => handleAttackToggle(decoyName, user.Name)}
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
              ))}
            </Box>
          </ConfigCard>
        ))}
    </Box>
  );
}

export default DecoysTab; 